import { Button, Typography, Box } from "@mui/material";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useCallback, useEffect, useRef, useState } from "react";
import ViewClientPhoneReasonDialog from "./ViewClientPhoneReasonDialog";
import {
  PatientDetailData,
  useLazyGetClientPhoneNumberQuery,
} from "../features/data/patientApiSlice";
import { useTranslation } from "react-i18next";
import { createStyleSheet } from "../utils/styles";
import { ViewClientPhoneLoggingPayload } from "../features/api/logging";
import { useSelector } from "react-redux";
import { logEvent } from "../features/api/apiSlice";
import { selectCurrentName } from "../features/auth/authSlice";
import { useAppDispatch } from "../features/store";

const PHONE_VISIBLE_DURATION_SECONDS = 90;
const PHONE_VISIBLE_DURATION_MS = PHONE_VISIBLE_DURATION_SECONDS * 1000;

type Props = {
  patient: PatientDetailData;
};

const ViewClientPhoneNumber = ({ patient }: Props) => {
  const { t } = useTranslation("ViewClientPhoneNumber");
  const dispatch = useAppDispatch();
  const userName = useSelector(selectCurrentName);

  const [viewPhoneReasonDialogOpen, setViewPhoneReasonDialogOpen] =
    useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  const timeoutRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  const [getClientPhoneNumber] = useLazyGetClientPhoneNumberQuery();

  const handleViewPhoneReasonClick = useCallback(() => {
    setViewPhoneReasonDialogOpen(true);
  }, []);

  const handleViewPhoneReasonDialogClose = useCallback(() => {
    setViewPhoneReasonDialogOpen(false);
  }, []);

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleSubmitReason = useCallback(
    (reason: string) => {
      const uid = patient?.uid;
      if (uid) {
        getClientPhoneNumber({
          uid,
          reason: reason,
        }).then((response) => {
          const clientPhone = response.data?.phone_number;
          if (clientPhone) {
            handleViewPhoneReasonDialogClose();
            setPhoneNumber(clientPhone);
            setRemainingSeconds(PHONE_VISIBLE_DURATION_SECONDS);

            clearTimers();

            // Countdown tick
            intervalRef.current = window.setInterval(() => {
              setRemainingSeconds((prev) => prev - 1);
            }, 1000);

            // Auto-hide
            timeoutRef.current = window.setTimeout(() => {
              clearTimers();
              setPhoneNumber(null);
              setRemainingSeconds(0);
            }, PHONE_VISIBLE_DURATION_MS);
          }
        });
        const payload: ViewClientPhoneLoggingPayload = {
          userName,
          reason,
          patientUid: patient?.uid ?? "",
        };
        logEvent(dispatch, {
          event: "viewClientPhoneNumber",
          data: payload,
        });
      }
    },
    [patient]
  );

  useEffect(() => {
    return () => {
      clearTimers();
      setPhoneNumber(null);
      setRemainingSeconds(0);
    };
  }, []);

  const isPhoneVisible = Boolean(phoneNumber);
  const formattedPhone =
    phoneNumber &&
    (parsePhoneNumberFromString(
      phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`
    )?.formatInternational() ?? phoneNumber);

  return (
    <>
      {isPhoneVisible ? (
        <Box sx={styles.phoneContainer} role="status" aria-live="polite">
          <Typography variant="body2" sx={styles.phoneNumberText}>
            {formattedPhone}
          </Typography>

          <Typography
            variant="body2"
            sx={styles.countdownText}
            aria-live="polite"
            aria-atomic="true"
          >
            {t("remainingTimeText", { seconds: remainingSeconds })}
          </Typography>
        </Box>
      ) : (
        <Button
          variant="text"
          onClick={handleViewPhoneReasonClick}
          aria-label={t("viewPhoneNumberAriaLabel")}
          sx={styles.viewPhoneBtn}
        >
          {t("viewPhoneNumber")}
        </Button>
      )}

      <ViewClientPhoneReasonDialog
        open={viewPhoneReasonDialogOpen}
        onClose={handleViewPhoneReasonDialogClose}
        onSubmit={handleSubmitReason}
      />
    </>
  );
};

const styles = createStyleSheet({
  phoneContainer: {
    ml: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  phoneNumberText: {
    fontWeight: "bold",
  },
  countdownText: {
    color: "primary.main",
    marginTop: 0.5,
  },
  viewPhoneBtn: { ml: 1, padding: "0 5px" },
});

export default ViewClientPhoneNumber;
